import { Component } from "@theme/component";

/**
 * Wraps one rendered size guide (snippets/size-guide.liquid) and puts the drawer's scroll hint on its
 * size tables, turned a quarter turn. Shared by the PDP surlayer (size-guide-layer.liquid) and the CMS
 * page section (sections/size-guide-page.liquid), so both get the same keyboard access to wide tables.
 *
 * Reads its translated fallback region name from `data-table-label` (no `| t` in JS).
 *
 * @extends {Component}
 */
class SizeGuideTables extends Component {
  /** @type {ResizeObserver | null} */
  #tableObserver = null;

  /** @type {number | null} */
  #tableHintRafId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#watchTables();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unwatchTables();
  }

  /**
   * A morph reuses this element instead of replacing it, so connectedCallback does NOT run again and
   * the tables it swapped in would keep neither their scroll listener nor the observer — meaning no
   * `tabindex`, hence no keyboard access. Reached by the PDP colour swap (product-color-swap.js) and
   * by quick-add, both of which can bring in a guide with a different number of tables.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#unwatchTables();
    this.#watchTables();
  }

  /**
   * Found by class rather than by `ref`: they belong to the vendored module (snippets/size-guide.liquid).
   * @returns {HTMLElement[]}
   */
  get #tableScrollers() {
    return Array.from(this.querySelectorAll(".size-guide__table-scroll")).filter((el) => el instanceof HTMLElement);
  }

  /**
   * A `ResizeObserver` covers every moment the hint needs recomputing without an explicit call: a
   * dialog opening (the scroller measures 0 while closed), the viewport rotating, or a hidden guide
   * being revealed by the typology switcher. What it does NOT cover is a scroller that did not exist
   * when this ran — hence `updatedCallback`, which rebinds after a morph.
   */
  #watchTables() {
    const scrollers = this.#tableScrollers;
    if (!scrollers.length || typeof ResizeObserver === "undefined") return;

    this.#tableObserver = new ResizeObserver(() => this.#scheduleTableHints());

    for (const scroller of scrollers) {
      scroller.addEventListener("scroll", this.#scheduleTableHints, { passive: true });
      this.#tableObserver.observe(scroller);
    }
  }

  #unwatchTables() {
    for (const scroller of this.#tableScrollers) {
      scroller.removeEventListener("scroll", this.#scheduleTableHints);
    }

    this.#tableObserver?.disconnect();
    this.#tableObserver = null;

    if (this.#tableHintRafId !== null) {
      cancelAnimationFrame(this.#tableHintRafId);
      this.#tableHintRafId = null;
    }
  }

  /** Coalesces a burst of scroll and resize callbacks into one paint, like `dialog.js` does. */
  #scheduleTableHints = () => {
    if (this.#tableHintRafId !== null) return;

    this.#tableHintRafId = requestAnimationFrame(() => {
      this.#tableHintRafId = null;
      for (const scroller of this.#tableScrollers) this.#updateTableHint(scroller);
    });
  };

  /**
   * Fades out whichever edge still has content behind it — the drawer's gradient mask, on the inline
   * axis — and makes the scroller reachable while it overflows.
   *
   * The mask is set on the TABLE and not on the scroller, unlike `dialog.js`: a mask erases the whole
   * rendering of the element that carries it, background included, so masking the scroller would have
   * shown the page through it. Masking the table keeps the scroller's grey behind the columns that
   * dissolve. The price is realigning the mask on every frame, since it is in table coordinates while
   * it must stay pinned to the visible window — hence `mask-position` and `mask-size`.
   *
   * A scroll container is not focusable by default in Chromium, so without `tabindex` a keyboard user
   * cannot reach the hidden columns at all (WCAG 2.1.1) — and a focusable container needs a role and
   * a name, or it is a nameless tab stop (WCAG 4.1.2). Both ride along on the same measurement, so a
   * table that fits keeps no tab stop.
   *
   * @param {HTMLElement} scroller
   */
  #updateTableHint(scroller) {
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    const scrollable = scrollWidth - clientWidth;
    const table = scroller.querySelector("table");

    // Sub-pixel layouts leave a fraction of overflow on tables that visually fit.
    if (scrollable <= 1) {
      if (table) {
        table.style.maskImage = "";
        table.style.maskPosition = "";
        table.style.maskSize = "";
        table.style.maskRepeat = "";
      }
      scroller.style.backgroundSize = "";
      scroller.removeAttribute("tabindex");
      scroller.removeAttribute("role");
      scroller.removeAttribute("aria-label");
      scroller.removeAttribute("aria-labelledby");
      return;
    }

    scroller.setAttribute("tabindex", "0");

    // A region has to be named, or it is not exposed as a landmark at all. The table's own heading
    // names it by subject, which keeps two tables of the same guide apart; `data-table-label` is the
    // generic fallback, rendered by the theme so that it comes out translated (no `| t` in JS).
    const labelledBy = scroller.dataset.regionLabelledby;
    const label = this.dataset.tableLabel;

    if (labelledBy) {
      scroller.setAttribute("role", "region");
      scroller.setAttribute("aria-labelledby", labelledBy);
      scroller.removeAttribute("aria-label");
    } else if (label) {
      scroller.setAttribute("role", "region");
      scroller.setAttribute("aria-label", label);
    }

    if (!table) return;

    // The fade is painted over the table's height, not the frame's: the horizontal scrollbar gutter
    // belongs to the frame, and a full background left a grey band under the table.
    scroller.style.backgroundSize = `100% ${table.offsetHeight}px`;

    const ratio = scrollLeft / scrollable;

    table.style.maskImage = `linear-gradient(
      to right,
      transparent ${ratio > 0 ? 1 : 0}%,
      black ${ratio < 0.1 ? ratio * 100 : 10}%,
      black ${ratio > 0.9 ? ratio * 100 : 90}%,
      transparent 100%
    )`;
    table.style.maskPosition = `${scrollLeft}px 0`;
    table.style.maskSize = `${clientWidth}px 100%`;
    table.style.maskRepeat = "no-repeat";
  }
}

if (!customElements.get("size-guide-tables")) {
  customElements.define("size-guide-tables", SizeGuideTables);
}
